// Derechos de privacidad del titular (VLT-289 · CCPA + estados).
// El cliente EXPORTA sus datos (JSON, servido por la app vía RLS — solo ve lo
// suyo) y SOLICITA borrado; la firma resuelve en una bandeja con plazo de 45
// días (§1798.130). El borrado JAMÁS gana a la ley: legal hold y el piso de
// retención (0012) bloquean la disposición — se resuelve con nota explicada.
import { supabase, hasBackend } from '@/lib/supabase';
import { returnsService } from '@/services/returns';
import { documentsService } from '@/services/documents';
import { requestsService } from '@/services/requests';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export type PrivacyKind = 'export' | 'deletion';
export type PrivacyStatus = 'received' | 'in_review' | 'completed' | 'denied';

export interface PrivacyRequest {
  id: string;
  clientId: string;
  clientName?: string;
  kind: PrivacyKind;
  status: PrivacyStatus;
  dueDate: string; // YYYY-MM-DD
  createdAt: string;
  resolutionNote?: string | null;
}

/** Días restantes del plazo CCPA (pura, testeable; negativo = vencida). */
export function daysLeft(dueDate: string, now = new Date()): number {
  const due = new Date(`${dueDate}T00:00:00`);
  return Math.ceil((due.getTime() - now.getTime()) / 86_400_000);
}

/** Paquete de exportación del titular (pura sobre datos ya cargados). */
export function buildExport(input: {
  profile: { fullName: string; email: string };
  returns: unknown[];
  documents: unknown[];
  requests: unknown[];
}): Record<string, unknown> {
  return {
    generatedAt: new Date().toISOString(),
    format: 'vaultex-export-v1',
    note: 'Datos personales visibles para el titular vía sus permisos (RLS). / Personal data visible to the data subject via their permissions (RLS).',
    profile: input.profile,
    taxReturns: input.returns,
    documents: input.documents,
    informationRequests: input.requests,
  };
}

export interface PrivacyService {
  /** Reúne los datos del titular autenticado para su descarga. */
  exportMyData(profile: { fullName: string; email: string }): Promise<Record<string, unknown>>;
  /** Crea la solicitud de borrado del titular (45 días de plazo). */
  requestDeletion(): Promise<{ ok: boolean; dueDate?: string }>;
  /** Solicitudes del titular. */
  mine(): Promise<PrivacyRequest[]>;
  /** Bandeja de la firma (staff), pendientes primero. */
  inbox(): Promise<PrivacyRequest[]>;
  /** Resolver (staff) con nota — obligatoria al denegar. */
  resolve(id: string, status: 'completed' | 'denied', note: string): Promise<boolean>;
}

// ─── Implementación real (Supabase, RLS) ────────────────────────────────────

interface Row {
  id: string; client_id: string; kind: PrivacyKind; status: PrivacyStatus;
  due_date: string; created_at: string; resolution_note: string | null;
  client?: { first_name: string; last_name: string } | null;
}

const rowTo = (r: Row): PrivacyRequest => ({
  id: r.id, clientId: r.client_id, kind: r.kind, status: r.status,
  dueDate: r.due_date, createdAt: r.created_at, resolutionNote: r.resolution_note,
  clientName: r.client ? `${r.client.first_name} ${r.client.last_name}`.trim() : undefined,
});

const myClientRow = async (): Promise<{ id: string; organization_id: string } | null> => {
  const { data: auth } = await sessionUser();
  if (!auth.user) return null;
  const { data } = await supabase.from('clients').select('id, organization_id').eq('profile_id', auth.user.id).limit(1);
  return ((data as { id: string; organization_id: string }[] | null) ?? [])[0] ?? null;
};

const realPrivacy: PrivacyService = {
  async exportMyData(profile) {
    const [returns, documents, requests] = await Promise.all([
      returnsService.list(), documentsService.list(), requestsService.listMine(),
    ]);
    return buildExport({ profile, returns, documents, requests });
  },

  async requestDeletion() {
    const { data: auth } = await sessionUser();
    const me = await myClientRow();
    if (!auth.user || !me) return { ok: false };
    const { data, error } = await supabase
      .from('privacy_requests')
      .insert({ organization_id: me.organization_id, client_id: me.id, requested_by: auth.user.id, kind: 'deletion' } as never)
      .select('due_date')
      .single();
    if (error) {
      console.warn('[VaulTex] privacy.request:', error.message);
      return { ok: false };
    }
    return { ok: true, dueDate: (data as { due_date: string }).due_date };
  },

  async mine() {
    const { data, error } = await supabase
      .from('privacy_requests')
      .select('id, client_id, kind, status, due_date, created_at, resolution_note')
      .order('created_at', { ascending: false });
    if (error) loadFailed('privacy.mine', error);
    return (data as unknown as Row[]).map(rowTo);
  },

  async inbox() {
    const { data, error } = await supabase
      .from('privacy_requests')
      .select('id, client_id, kind, status, due_date, created_at, resolution_note, client:clients(first_name, last_name)')
      .order('due_date', { ascending: true })
      .limit(100);
    if (error) loadFailed('privacy.inbox', error);
    return (data as unknown as Row[]).map(rowTo);
  },

  async resolve(id, status, note) {
    const { data: auth } = await sessionUser();
    const { error } = await supabase
      .from('privacy_requests')
      .update({ status, resolution_note: note, resolved_by: auth.user?.id ?? null, resolved_at: new Date().toISOString() } as never)
      .eq('id', id);
    if (error) console.warn('[VaulTex] privacy.resolve:', error.message);
    return !error;
  },
};

// ─── Implementación demo (fixtures) ─────────────────────────────────────────

const plus45 = () => new Date(Date.now() + 45 * 86_400_000).toISOString().slice(0, 10);

const DEMO: PrivacyRequest[] = [
  { id: 'pr1', clientId: 'c3', clientName: 'Lucía Gómez', kind: 'deletion', status: 'received', dueDate: '2026-09-20', createdAt: '2026-08-06', resolutionNote: null },
];

const fixturePrivacy: PrivacyService = {
  exportMyData: async (profile) => {
    const [returns, documents, requests] = await Promise.all([
      returnsService.list(), documentsService.list(), requestsService.listMine(),
    ]);
    return buildExport({ profile, returns, documents, requests });
  },
  requestDeletion: () => {
    const dueDate = plus45();
    DEMO.unshift({ id: `pr${DEMO.length + 1}`, clientId: 'c1', clientName: 'María Santos', kind: 'deletion', status: 'received', dueDate, createdAt: new Date().toISOString().slice(0, 10), resolutionNote: null });
    return Promise.resolve({ ok: true, dueDate });
  },
  mine: () => Promise.resolve(structuredClone(DEMO.filter((r) => r.clientId === 'c1'))),
  inbox: () => Promise.resolve(structuredClone(DEMO)),
  resolve: (id, status, note) => {
    const r = DEMO.find((x) => x.id === id);
    if (r) {
      r.status = status;
      r.resolutionNote = note;
    }
    return Promise.resolve(Boolean(r));
  },
};

export const privacyService: PrivacyService = hasBackend ? realPrivacy : fixturePrivacy;
